import React, { useEffect, useRef } from 'react';
import Phaser from 'phaser';
import MainScene from '../game/scenes/MainScene';
import UIScene from '../game/scenes/UIScene';
import { getAudioContext } from '../game/audioContext';
import { RENDER_SCALE } from '../game/renderScale';

const GameCanvas = ({ wallet, tier, skin, ticketId }) => {
  const gameRef = useRef(null);

  useEffect(() => {
    // Pass data to Phaser globally
    window.GAME_INITIAL_DATA = { wallet, tier, skin, ticketId };

    // Il contesto audio lo possiede l'app, non Phaser: cosi' non viene chiuso a
    // ogni uscita dalla partita. Vedi game/audioContext.js per il perche' —
    // in breve, evita di esaurire gli AudioContext concessi dal browser e gli
    // errori "Cannot suspend/resume a closed AudioContext".
    const audioContext = getAudioContext();

    // Configurazione di Phaser
    const config = {
      type: Phaser.AUTO,
      parent: 'phaser-container',
      ...(audioContext ? { audio: { context: audioContext } } : {}),
      // La misura del gioco e' in pixel FISICI, la misura del canvas sullo
      // schermo resta in pixel CSS. Vedi game/renderScale.js per il perche':
      // in breve, `Scale.RESIZE` disegnerebbe a meta' risoluzione su Retina, e
      // i power-up — l'oggetto piu' piccolo e piu' dettagliato in scena — sono
      // i primi a sfocare.
      //
      // `Scale.NONE` invece di RESIZE perche' RESIZE ignora `zoom` e riporta
      // sempre il canvas alla misura CSS del genitore (ScaleManager.js, ramo
      // RESIZE): ci ripiglierebbe la risoluzione a ogni ridisegno. Il prezzo e'
      // che il ridimensionamento della finestra dobbiamo seguirlo noi, qui
      // sotto — su mobile succede in continuazione (barra URL, rotazione).
      scale: {
        mode: Phaser.Scale.NONE,
        width: Math.round(window.innerWidth * RENDER_SCALE),
        height: Math.round(window.innerHeight * RENDER_SCALE),
        zoom: 1 / RENDER_SCALE,
        autoCenter: Phaser.Scale.NO_CENTER,
      },
      physics: {
        default: 'arcade',
        arcade: { debug: false },
      },
      scene: [MainScene, UIScene],
    };

    const game = new Phaser.Game(config);
    gameRef.current = game;

    // Ridimensionamento: con `Scale.NONE` Phaser non ascolta la finestra, quindi
    // lo facciamo noi. La misura del gioco va in pixel fisici, quella in CSS la
    // scriviamo a mano sul canvas: in questa modalita' Phaser aggiorna lo stile
    // solo quando cambia lo zoom, e senza queste due righe il canvas resterebbe
    // largo il doppio dello schermo.
    const adattaAllaFinestra = () => {
      // Phaser crea il canvas durante il boot, che e' asincrono: chiamata
      // troppo presto, questa funzione troverebbe `game.canvas` indefinito.
      if (!game.canvas) return;
      const larghezza = window.innerWidth;
      const altezza = window.innerHeight;
      game.scale.resize(
        Math.round(larghezza * RENDER_SCALE),
        Math.round(altezza * RENDER_SCALE)
      );
      game.canvas.style.width = larghezza + 'px';
      game.canvas.style.height = altezza + 'px';
    };
    // La prima volta si aspetta il boot; da li' in poi bastano gli eventi.
    game.events.once('ready', adattaAllaFinestra);
    window.addEventListener('resize', adattaAllaFinestra);
    window.addEventListener('orientationchange', adattaAllaFinestra);

    // Aggancio per diagnosticare l'input dalla console del browser: Phaser non
    // espone la partita da nessuna parte, e senza questo per guardare lo stato
    // di una scena bisogna ricompilare con dei `console.log` dentro. Solo fuori
    // produzione — la pulizia qui sotto lo dava gia' per esistente.
    if (process.env.NODE_ENV !== 'production') {
      window.__vexelGames = window.__vexelGames || [];
      window.__vexelGames.push(game);
    }

    // --- CLEANUP ---
    return () => {
      window.removeEventListener('resize', adattaAllaFinestra);
      window.removeEventListener('orientationchange', adattaAllaFinestra);
      // 2. IMPORTANTE: Forza la disconnessione del socket se è ancora aperto
      if (game.scene) {
        const mainScene = game.scene.getScene('MainScene');
        if (mainScene) {
          // FIX: Emetti shutdown esplicitamente per pulire i window event listeners
          mainScene.events.emit('shutdown');
          if (mainScene.socket) {
            mainScene.socket.disconnect();
          }
        }
      }

      // 1. Distruggi il gioco Phaser
      game.destroy(true);

      if (process.env.NODE_ENV !== 'production' && window.__vexelGames) {
        window.__vexelGames = window.__vexelGames.filter((g) => g !== game);
      }

      gameRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 100dvh e non 100vh: su iOS 100vh include la barra dell'indirizzo e taglia
  // il fondo del canvas (cash out e joystick finivano sotto il bordo).
  return (
    <div
      id="phaser-container"
      style={{ width: '100%', height: '100dvh', overflow: 'hidden' }}
    />
  );
};

export default GameCanvas;